import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowLeft, Loader2, Plus, Save, Trash2, Compass, ArrowUp, ArrowDown,
  Eye, EyeOff, ExternalLink, CheckCircle2, Image as ImageIcon, X,
  FileText, Pencil, FolderOpen, ChevronRight, Layers, CornerUpLeft,
} from "lucide-react";
import { Header } from "../components/Header";
import MediaPicker from "../components/MediaPicker";
import { api, formatApiErrorDetail, resolveAsset } from "../lib/api";

export default function AdminNavItems() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [newLabel, setNewLabel] = useState("");
  const [newTo, setNewTo] = useState("");
  const [newTarget, setNewTarget] = useState("self");
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/admin/nav-items");
      setItems(r.data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const topLevel = useMemo(
    () => (items || []).filter((it) => !it.parent_id),
    [items],
  );
  const children = useMemo(
    () => (items || []).filter((it) => it.parent_id === selectedId),
    [items, selectedId],
  );
  const selectedItem = useMemo(
    () => topLevel.find((it) => it.id === selectedId) || null,
    [topLevel, selectedId],
  );

  // Auto-deselect if the selected top-level item got deleted or demoted
  // into someone else's sub-items (so the right panel never points at
  // a stale/nonexistent parent).
  useEffect(() => {
    if (items && selectedId && !topLevel.some((it) => it.id === selectedId)) {
      setSelectedId(null);
    }
  }, [items, selectedId, topLevel]);

  const onCreateTop = async (e) => {
    e.preventDefault();
    if (!newLabel.trim() || !newTo.trim()) return;
    setCreating(true);
    setError("");
    try {
      await api.post("/admin/nav-items", {
        label: newLabel.trim(),
        to: newTo.trim(),
        target: newTarget,
      });
      setNewLabel("");
      setNewTo("");
      setNewTarget("self");
      await refresh();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  const updatePosition = async (item, position) => {
    if (item.kind === "page") {
      await api.put(`/admin/custom-pages/${item.slug}`, { position });
    } else {
      await api.put(`/admin/nav-items/${item.id}`, { position });
    }
  };

  // Reorders are scoped to whichever group the row belongs to (all
  // top-level items, or all siblings under one parent) — swapping just
  // the two `position` values between the row and its in-group
  // neighbour, so it never jumps across branches like the old global
  // "move" endpoint could.
  const moveWithinGroup = async (group, item, direction) => {
    const idx = group.findIndex((g) => g.id === item.id);
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= group.length) return;
    const other = group[targetIdx];
    setError("");
    try {
      await Promise.all([
        updatePosition(item, other.position),
        updatePosition(other, item.position),
      ]);
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const setParent = async (item, parentId) => {
    setError("");
    try {
      if (item.kind === "page") {
        await api.put(`/admin/custom-pages/${item.slug}`, { nav_parent_id: parentId || "" });
      } else {
        await api.put(`/admin/nav-items/${item.id}`, { parent_id: parentId || "" });
      }
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const childCountFor = (id) => (items || []).filter((it) => it.parent_id === id).length;

  return (
    <div
      className="admin-chrome min-h-screen bg-white text-[var(--brand-ink)]"
      data-testid="page-admin-nav"
    >
      <Header />
      <main className="mx-auto max-w-6xl px-5 py-10 sm:px-8 sm:py-14">
        <RouterLink
          to="/admin"
          className="inline-flex items-center gap-1.5 label-kicker text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
        >
          <ArrowLeft size={12} /> Back to admin
        </RouterLink>

        <div className="mt-6 mb-8">
          <div className="flex items-center gap-2 text-brand-accent">
            <Compass size={16} strokeWidth={2.5} />
            <span className="label-kicker">Header navigation</span>
          </div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
            Edit the menu buttons.
          </h1>
          <p className="mt-2 text-sm text-[var(--brand-body)]">
            Top-level buttons live on the left. Click one to manage its
            sub-items on the right — add, edit, reorder, or delete them
            without hunting through one long list. Custom Pages flagged
            "Show in nav" appear here too; edit those rows from the Custom
            Pages admin.
            {" "}
            <strong className="text-[var(--brand-ink)]">Tip:</strong> add an image + tile colour to any menu item and it'll also start showing up on the home page's daily-rotating "Explore" picks.
          </p>
        </div>

        {error ? (
          <p
            className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
            data-testid="nav-items-error"
          >
            {error}
          </p>
        ) : null}

        {items === null ? (
          <p className="text-sm text-[var(--brand-body)]">Loading…</p>
        ) : (
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Top-level panel */}
            <div data-testid="nav-toplevel-panel">
              <p className="mb-3 label-kicker text-[var(--brand-body)]">
                Top-level menu items
              </p>

              <form
                onSubmit={onCreateTop}
                className="mb-4 rounded-md border border-black/10 bg-[#F8FAFC] p-4"
                data-testid="nav-create-form"
              >
                <p className="label-kicker text-[var(--brand-body)]">Add a top-level button</p>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <input
                    type="text"
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    maxLength={40}
                    placeholder="Label (e.g. Forum)"
                    data-testid="nav-new-label"
                    className="rounded-md border border-black/15 bg-white px-3 py-2 text-sm"
                  />
                  <input
                    type="text"
                    value={newTo}
                    onChange={(e) => setNewTo(e.target.value)}
                    maxLength={200}
                    placeholder="/thelastpost or https://…"
                    data-testid="nav-new-to"
                    className="rounded-md border border-black/15 bg-white px-3 py-2 font-mono text-xs"
                  />
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <select
                    value={newTarget}
                    onChange={(e) => setNewTarget(e.target.value)}
                    data-testid="nav-new-target"
                    className="rounded-md border border-black/15 bg-white px-2 py-2 text-sm"
                  >
                    <option value="self">Same tab</option>
                    <option value="blank">New tab</option>
                  </select>
                  <button
                    type="submit"
                    disabled={creating || !newLabel.trim() || !newTo.trim()}
                    data-testid="nav-create-submit"
                    className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-semibold text-white hover:bg-brand-accent disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                    Add
                  </button>
                </div>
              </form>

              {topLevel.length === 0 ? (
                <p className="text-sm text-[var(--brand-body)]">No top-level menu items yet.</p>
              ) : (
                <div className="space-y-3" data-testid="nav-toplevel-list">
                  {topLevel.map((it, i) =>
                    it.kind === "page" ? (
                      <NavPageRow
                        key={it.id}
                        item={it}
                        mode="top"
                        upDisabled={i === 0}
                        downDisabled={i === topLevel.length - 1}
                        onMove={(dir) => moveWithinGroup(topLevel, it, dir)}
                        parentOptions={topLevel}
                        onSetParent={setParent}
                        selected={it.id === selectedId}
                        onSelect={() => setSelectedId(it.id)}
                        childCount={childCountFor(it.id)}
                        onSaved={refresh}
                        setOuterError={setError}
                      />
                    ) : (
                      <NavRow
                        key={it.id}
                        item={it}
                        mode="top"
                        upDisabled={i === 0}
                        downDisabled={i === topLevel.length - 1}
                        onMove={(dir) => moveWithinGroup(topLevel, it, dir)}
                        parentOptions={topLevel}
                        onSetParent={setParent}
                        selected={it.id === selectedId}
                        onSelect={() => setSelectedId(it.id)}
                        childCount={childCountFor(it.id)}
                        onSaved={refresh}
                        setOuterError={setError}
                      />
                    ),
                  )}
                </div>
              )}
            </div>

            {/* Sub-items panel */}
            <div data-testid="nav-subitems-panel">
              {!selectedItem ? (
                <div
                  className="flex h-full min-h-[200px] items-center justify-center rounded-md border border-dashed border-black/15 p-8 text-center"
                  data-testid="nav-subitems-empty"
                >
                  <p className="text-sm text-[var(--brand-body)]">
                    Select a top-level item on the left to manage its sub-items.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <p className="label-kicker text-[var(--brand-body)]">
                      Sub-items of <span className="text-[var(--brand-ink)]">{selectedItem.label}</span>
                    </p>
                    <button
                      type="button"
                      onClick={() => setSelectedId(null)}
                      data-testid="nav-subitems-close"
                      className="text-xs text-[var(--brand-body)] underline-offset-2 hover:underline"
                    >
                      Close
                    </button>
                  </div>

                  <SubItemCreateForm
                    parentId={selectedItem.id}
                    onCreated={refresh}
                    setOuterError={setError}
                  />

                  {children.length === 0 ? (
                    <p className="mt-4 text-sm text-[var(--brand-body)]" data-testid="nav-subitems-list-empty">
                      No sub-items yet — add one above.
                    </p>
                  ) : (
                    <div className="mt-4 space-y-3" data-testid="nav-subitems-list">
                      {children.map((it, i) =>
                        it.kind === "page" ? (
                          <NavPageRow
                            key={it.id}
                            item={it}
                            mode="child"
                            upDisabled={i === 0}
                            downDisabled={i === children.length - 1}
                            onMove={(dir) => moveWithinGroup(children, it, dir)}
                            parentOptions={topLevel}
                            currentParentId={selectedItem.id}
                            onSetParent={setParent}
                            onSaved={refresh}
                            setOuterError={setError}
                          />
                        ) : (
                          <NavRow
                            key={it.id}
                            item={it}
                            mode="child"
                            upDisabled={i === 0}
                            downDisabled={i === children.length - 1}
                            onMove={(dir) => moveWithinGroup(children, it, dir)}
                            parentOptions={topLevel}
                            currentParentId={selectedItem.id}
                            onSetParent={setParent}
                            onSaved={refresh}
                            setOuterError={setError}
                          />
                        ),
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function SubItemCreateForm({ parentId, onCreated, setOuterError }) {
  const [label, setLabel] = useState("");
  const [to, setTo] = useState("");
  const [target, setTarget] = useState("self");
  const [creating, setCreating] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!label.trim() || !to.trim()) return;
    setCreating(true);
    setOuterError("");
    try {
      await api.post("/admin/nav-items", {
        label: label.trim(),
        to: to.trim(),
        target,
        parent_id: parentId,
      });
      setLabel("");
      setTo("");
      setTarget("self");
      await onCreated();
    } catch (err) {
      setOuterError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-md border border-black/10 bg-[#F8FAFC] p-4"
      data-testid="nav-subitem-create-form"
    >
      <p className="label-kicker text-[var(--brand-body)]">Add a sub-item</p>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={40}
          placeholder="Label"
          data-testid="nav-subitem-new-label"
          className="rounded-md border border-black/15 bg-white px-3 py-2 text-sm"
        />
        <input
          type="text"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          maxLength={200}
          placeholder="/page or https://…"
          data-testid="nav-subitem-new-to"
          className="rounded-md border border-black/15 bg-white px-3 py-2 font-mono text-xs"
        />
      </div>
      <div className="mt-2 flex items-center gap-2">
        <select
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          data-testid="nav-subitem-new-target"
          className="rounded-md border border-black/15 bg-white px-2 py-2 text-sm"
        >
          <option value="self">Same tab</option>
          <option value="blank">New tab</option>
        </select>
        <button
          type="submit"
          disabled={creating || !label.trim() || !to.trim()}
          data-testid="nav-subitem-create-submit"
          className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-semibold text-white hover:bg-brand-accent disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Add
        </button>
      </div>
    </form>
  );
}

function NavRow({
  item, mode, upDisabled, downDisabled, onMove, parentOptions, currentParentId, onSetParent,
  selected, onSelect, childCount, onSaved, setOuterError,
}) {
  const [label, setLabel] = useState(item.label);
  const [to, setTo] = useState(item.to);
  const [target, setTarget] = useState(item.target);
  const [imageUrl, setImageUrl] = useState(item.image_url || "");
  const [tileColor, setTileColor] = useState(item.tile_color || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setLabel(item.label);
    setTo(item.to);
    setTarget(item.target);
    setImageUrl(item.image_url || "");
    setTileColor(item.tile_color || "");
  }, [item.label, item.to, item.target, item.image_url, item.tile_color]);

  const dirty =
    label.trim() !== item.label ||
    to.trim() !== item.to ||
    target !== item.target ||
    imageUrl !== (item.image_url || "") ||
    tileColor !== (item.tile_color || "");

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    setOuterError("");
    try {
      await api.put(`/admin/nav-items/${item.id}`, {
        label: label.trim(),
        to: to.trim(),
        target,
        image_url: imageUrl,
        tile_color: tileColor,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      await onSaved();
    } catch (e) {
      setOuterError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async () => {
    setOuterError("");
    try {
      await api.put(`/admin/nav-items/${item.id}`, { enabled: !item.enabled });
      await onSaved();
    } catch (e) {
      setOuterError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async () => {
    if (item.builtin) return;
    const warn = childCount
      ? ` Its ${childCount} sub-item(s) will move back to the top level.`
      : "";
    if (!window.confirm(`Delete the "${item.label}" menu button?${warn}`)) return;
    setOuterError("");
    try {
      await api.delete(`/admin/nav-items/${item.id}`);
      await onSaved();
    } catch (e) {
      setOuterError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setOuterError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImageUrl(r.data.url || "");
    } catch (err) {
      setOuterError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const previewBg = imageUrl ? resolveAsset(imageUrl) : "";

  const nestChoices = (parentOptions || []).filter(
    (o) => o.id !== item.id && o.id !== currentParentId,
  );

  return (
    <div
      className={`rounded-md border ${item.enabled ? "border-black/10" : "border-dashed border-black/15 opacity-70"} ${
        mode === "top" && selected ? "ring-2 ring-[#6CABDD]" : ""
      } bg-white p-4`}
      data-testid={`nav-row-${item.id}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onMove("up")}
          disabled={upDisabled}
          data-testid={`nav-row-${item.id}-up`}
          title="Move up"
          className="rounded-md border border-black/10 px-2 py-1.5 text-sm hover:border-[#0A0F17] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ArrowUp size={14} />
        </button>
        <button
          type="button"
          onClick={() => onMove("down")}
          disabled={downDisabled}
          data-testid={`nav-row-${item.id}-down`}
          title="Move down"
          className="rounded-md border border-black/10 px-2 py-1.5 text-sm hover:border-[#0A0F17] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ArrowDown size={14} />
        </button>

        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={40}
          data-testid={`nav-row-${item.id}-label`}
          className="w-28 rounded-md border border-black/15 bg-white px-2 py-1.5 text-sm"
        />
        <input
          type="text"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          maxLength={200}
          data-testid={`nav-row-${item.id}-to`}
          className="min-w-[140px] flex-1 rounded-md border border-black/15 bg-white px-2 py-1.5 font-mono text-xs"
        />
        <select
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          data-testid={`nav-row-${item.id}-target`}
          className="rounded-md border border-black/15 bg-white px-2 py-1.5 text-sm"
        >
          <option value="self">Same tab</option>
          <option value="blank">New tab</option>
        </select>

        {item.builtin ? (
          <span className="rounded-sm border border-black/10 px-2 py-0.5 text-xs text-[var(--brand-body)]">
            Built-in
          </span>
        ) : null}

        <div className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            data-testid={`nav-row-${item.id}-save`}
            className="inline-flex items-center gap-1 rounded-md bg-[#0A0F17] px-2 py-1.5 text-xs font-semibold text-white hover:bg-brand-accent disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Save
          </button>
          {saved ? (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
              <CheckCircle2 size={12} />
            </span>
          ) : null}

          <button
            type="button"
            onClick={toggleEnabled}
            data-testid={`nav-row-${item.id}-toggle`}
            title={item.enabled ? "Hide from public site" : "Show on public site"}
            className={`inline-flex items-center gap-1 rounded-md border px-2 py-1.5 text-xs ${
              item.enabled
                ? "border-black/10 text-[var(--brand-ink)] hover:border-[#0A0F17]"
                : "border-[#6CABDD]/40 bg-[#6CABDD]/10 text-[var(--brand-ink)]"
            }`}
          >
            {item.enabled ? <Eye size={12} /> : <EyeOff size={12} />}
            {item.enabled ? "Visible" : "Hidden"}
          </button>

          <a
            href={/^https?:\/\//i.test(to) ? to : to.startsWith("/") ? to : `/${to}`}
            target={target === "blank" ? "_blank" : "_self"}
            rel="noreferrer"
            data-testid={`nav-row-${item.id}-preview`}
            className="inline-flex items-center gap-1 rounded-md border border-black/10 px-2 py-1.5 text-xs hover:border-[#0A0F17]"
          >
            <ExternalLink size={12} />
          </a>

          {!item.builtin ? (
            <button
              type="button"
              onClick={remove}
              data-testid={`nav-row-${item.id}-delete`}
              className="inline-flex items-center gap-1 rounded-md border border-[#E63946]/40 px-2 py-1.5 text-xs text-[#E63946] hover:bg-[#E63946] hover:text-white"
            >
              <Trash2 size={12} />
            </button>
          ) : null}
        </div>
      </div>

      {/* Mode-specific nesting control + (top mode) manage-sub-items link */}
      <div
        className="mt-3 flex flex-wrap items-center gap-3 border-t border-dashed border-black/10 pt-3"
        data-testid={`nav-row-${item.id}-nest-row`}
      >
        {mode === "top" ? (
          <>
            <button
              type="button"
              onClick={onSelect}
              data-testid={`nav-toplevel-select-${item.id}`}
              className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs font-semibold ${
                selected
                  ? "border-[#6CABDD] bg-[#6CABDD]/10 text-[var(--brand-ink)]"
                  : "border-black/10 text-[var(--brand-ink)] hover:border-[#0A0F17]"
              }`}
            >
              <Layers size={12} />
              {childCount > 0 ? `${childCount} sub-item${childCount === 1 ? "" : "s"}` : "Manage sub-items"}
              <ChevronRight size={12} />
            </button>
            <label className="flex items-center gap-2">
              <span className="label-kicker text-[var(--brand-body)]">Nest under</span>
              <select
                value=""
                onChange={(e) => e.target.value && onSetParent(item, e.target.value)}
                data-testid={`nav-row-${item.id}-parent`}
                className="rounded-md border border-black/15 bg-white px-2 py-1.5 text-sm"
              >
                <option value="">— Stays top-level —</option>
                {nestChoices.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                    {o.kind === "page" ? " · custom page" : ""}
                  </option>
                ))}
              </select>
            </label>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => onSetParent(item, "")}
              data-testid={`nav-child-move-top-${item.id}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-black/10 px-2 py-1.5 text-xs font-semibold text-[var(--brand-ink)] hover:border-[#0A0F17]"
            >
              <CornerUpLeft size={12} /> Move to top level
            </button>
            <label className="flex items-center gap-2">
              <span className="label-kicker text-[var(--brand-body)]">Move to another parent</span>
              <select
                value=""
                onChange={(e) => e.target.value && onSetParent(item, e.target.value)}
                data-testid={`nav-row-${item.id}-reparent`}
                className="rounded-md border border-black/15 bg-white px-2 py-1.5 text-sm"
              >
                <option value="">— Choose a top-level item —</option>
                {nestChoices.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                    {o.kind === "page" ? " · custom page" : ""}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}
      </div>

      {/* Home-tile customisation — image + colour shown on the rotating home tiles */}
      <div
        className="mt-3 flex flex-wrap items-center gap-3 border-t border-dashed border-black/10 pt-3"
        data-testid={`nav-row-${item.id}-tile-settings`}
      >
        <span className="label-kicker text-[var(--brand-body)]">
          Home tile
        </span>

        <div
          className="h-12 w-20 flex-shrink-0 overflow-hidden rounded-sm border border-black/10 bg-cover bg-center"
          style={{
            backgroundColor: tileColor || "#002d75",
            backgroundImage: previewBg ? `url(${previewBg})` : undefined,
          }}
          aria-label="Tile preview"
          data-testid={`nav-row-${item.id}-tile-preview`}
        />

        <label className="flex items-center gap-1.5">
          <span className="label-kicker text-[var(--brand-body)]">Colour</span>
          <input
            type="color"
            value={tileColor || "#002d75"}
            onChange={(e) => setTileColor(e.target.value)}
            data-testid={`nav-row-${item.id}-tile-color`}
            className="h-8 w-10 cursor-pointer rounded-md border border-black/15 bg-white"
          />
          <input
            type="text"
            value={tileColor}
            onChange={(e) => setTileColor(e.target.value)}
            placeholder="#002d75"
            maxLength={7}
            pattern="^#[A-Fa-f0-9]{6}$"
            data-testid={`nav-row-${item.id}-tile-color-hex`}
            className="w-24 rounded-md border border-black/15 bg-white px-2 py-1.5 font-mono text-xs"
          />
          {tileColor ? (
            <button
              type="button"
              onClick={() => setTileColor("")}
              data-testid={`nav-row-${item.id}-tile-color-clear`}
              className="text-xs text-[var(--brand-body)] underline-offset-2 hover:underline"
            >
              clear
            </button>
          ) : null}
        </label>

        <div className="flex items-center gap-1.5">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={handleImageUpload}
            data-testid={`nav-row-${item.id}-image-input`}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid={`nav-row-${item.id}-image-upload`}
            className="inline-flex items-center gap-1.5 rounded-md border border-black/10 px-2 py-1.5 text-xs hover:border-[#0A0F17] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <ImageIcon size={12} />
            )}
            {imageUrl ? "Replace image" : "Upload image"}
          </button>
          <button
            type="button"
            onClick={() => setLibraryOpen(true)}
            disabled={uploading}
            data-testid={`nav-row-${item.id}-image-library`}
            title="Choose from your media library"
            className="inline-flex items-center gap-1.5 rounded-md border border-black/10 px-2 py-1.5 text-xs hover:border-[#0A0F17] disabled:opacity-40"
          >
            <FolderOpen size={12} /> From library
          </button>
          {imageUrl ? (
            <button
              type="button"
              onClick={() => setImageUrl("")}
              data-testid={`nav-row-${item.id}-image-clear`}
              title="Remove image"
              className="inline-flex items-center rounded-md border border-black/10 px-2 py-1.5 text-xs hover:border-[#E63946] hover:text-[#E63946]"
            >
              <X size={12} />
            </button>
          ) : null}
        </div>

        <span className="ml-auto text-xs text-[var(--brand-body)]">
          Optional — shown when this page is featured on the home rotation.
        </span>
      </div>
      <MediaPicker
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onSelect={(url) => {
          if (url) setImageUrl(url);
          setLibraryOpen(false);
        }}
        accept="image"
      />
    </div>
  );
}


function NavPageRow({
  item, mode, upDisabled, downDisabled, onMove, parentOptions, currentParentId, onSetParent,
  selected, onSelect, childCount, onSaved, setOuterError,
}) {
  // Read-only view of a custom-page-as-nav row. Reorder + visit; full edits
  // happen in /admin/custom-pages. The one exception is the nesting
  // control below — updates flow via a targeted PUT so admins don't have
  // to hop tabs just to nest/un-nest a custom page.
  const [savingParent, setSavingParent] = useState(false);

  const nestChoices = (parentOptions || []).filter(
    (o) => o.id !== item.id && o.id !== currentParentId,
  );

  const saveParent = async (nextParent) => {
    setSavingParent(true);
    setOuterError("");
    try {
      await onSetParent(item, nextParent);
    } finally {
      setSavingParent(false);
    }
  };

  return (
    <div
      className={`rounded-md border border-dashed border-[#6CABDD]/50 bg-[#6CABDD]/5 p-4 ${
        mode === "top" && selected ? "ring-2 ring-[#6CABDD]" : ""
      }`}
      data-testid={`nav-row-${item.id}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onMove("up")}
          disabled={upDisabled}
          data-testid={`nav-row-${item.id}-up`}
          title="Move up"
          className="rounded-md border border-black/10 px-2 py-1.5 text-sm hover:border-[#0A0F17] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ArrowUp size={14} />
        </button>
        <button
          type="button"
          onClick={() => onMove("down")}
          disabled={downDisabled}
          data-testid={`nav-row-${item.id}-down`}
          title="Move down"
          className="rounded-md border border-black/10 px-2 py-1.5 text-sm hover:border-[#0A0F17] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ArrowDown size={14} />
        </button>

        <div className="flex w-28 items-center gap-1.5 rounded-md border border-black/10 bg-white px-2 py-1.5 text-sm">
          <FileText size={12} className="text-[var(--brand-body)]" />
          <span className="truncate font-semibold">{item.label}</span>
        </div>
        <div className="min-w-[140px] flex-1 rounded-md border border-black/10 bg-white px-2 py-1.5 font-mono text-xs text-[var(--brand-body)]">
          {item.to}
        </div>
        <span
          className="rounded-sm border border-[#6CABDD]/40 bg-[#6CABDD]/10 px-2 py-0.5 text-xs text-[var(--brand-ink)]"
          title="This row is a Custom Page flagged Show in nav. Edit it from the Custom Pages admin."
        >
          Custom page
        </span>

        <div className="ml-auto flex items-center gap-1.5">
          <RouterLink
            to={`/admin/custom-pages?focus=${encodeURIComponent(item.slug)}`}
            data-testid={`nav-row-${item.id}-edit`}
            className="inline-flex items-center gap-1 rounded-md bg-[#0A0F17] px-2 py-1.5 text-xs font-semibold text-white hover:bg-brand-accent"
            title="Edit in Custom Pages"
          >
            <Pencil size={12} />
            Edit
          </RouterLink>

          <a
            href={item.to}
            data-testid={`nav-row-${item.id}-preview`}
            className="inline-flex items-center gap-1 rounded-md border border-black/10 px-2 py-1.5 text-xs hover:border-[#0A0F17]"
          >
            <ExternalLink size={12} />
          </a>
        </div>
      </div>

      <div
        className="mt-3 flex flex-wrap items-center gap-3 border-t border-dashed border-black/10 pt-3"
        data-testid={`nav-row-${item.id}-nest-row`}
      >
        {mode === "top" ? (
          <>
            <button
              type="button"
              onClick={onSelect}
              data-testid={`nav-toplevel-select-${item.id}`}
              className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs font-semibold ${
                selected
                  ? "border-[#6CABDD] bg-[#6CABDD]/10 text-[var(--brand-ink)]"
                  : "border-black/10 text-[var(--brand-ink)] hover:border-[#0A0F17]"
              }`}
            >
              <Layers size={12} />
              {childCount > 0 ? `${childCount} sub-item${childCount === 1 ? "" : "s"}` : "Manage sub-items"}
              <ChevronRight size={12} />
            </button>
            <label className="flex items-center gap-2">
              <span className="label-kicker text-[var(--brand-body)]">Nest under</span>
              <select
                value=""
                disabled={savingParent}
                onChange={(e) => e.target.value && saveParent(e.target.value)}
                data-testid={`nav-row-${item.id}-parent`}
                className="rounded-md border border-black/15 bg-white px-2 py-1.5 text-sm disabled:opacity-60"
              >
                <option value="">— Stays top-level —</option>
                {nestChoices.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                    {o.kind === "page" ? " · custom page" : ""}
                  </option>
                ))}
              </select>
            </label>
            {savingParent ? <Loader2 size={12} className="animate-spin text-[var(--brand-body)]" /> : null}
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => saveParent("")}
              disabled={savingParent}
              data-testid={`nav-child-move-top-${item.id}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-black/10 px-2 py-1.5 text-xs font-semibold text-[var(--brand-ink)] hover:border-[#0A0F17] disabled:opacity-60"
            >
              {savingParent ? <Loader2 size={12} className="animate-spin" /> : <CornerUpLeft size={12} />}
              Move to top level
            </button>
            <label className="flex items-center gap-2">
              <span className="label-kicker text-[var(--brand-body)]">Move to another parent</span>
              <select
                value=""
                disabled={savingParent}
                onChange={(e) => e.target.value && saveParent(e.target.value)}
                data-testid={`nav-row-${item.id}-reparent`}
                className="rounded-md border border-black/15 bg-white px-2 py-1.5 text-sm disabled:opacity-60"
              >
                <option value="">— Choose a top-level item —</option>
                {nestChoices.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                    {o.kind === "page" ? " · custom page" : ""}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}
      </div>
    </div>
  );
}
